import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, message, Tooltip, Modal } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAllClient } from '../../api/idsClient';
import './index.css';
import dayjs from 'dayjs';

const CDN_PREFIX = 'https://cdn.upyun.mallimg.shjinjia.com.cn/';

// 兼容接口返回的 camelCase 与 PascalCase
const getField = (record: Record<string, unknown>, fieldCode: string): unknown => {
  const val = record[fieldCode];
  if (val !== undefined && val !== null) return val;
  const camel = fieldCode.charAt(0).toLowerCase() + fieldCode.slice(1);
  return record[camel];
};

// 客户端密钥：取 clientSecrets?.[0].content
const getClientSecretsDisplay = (record: Record<string, unknown>): string => {
  const raw = record.clientSecrets ?? record.ClientSecrets;
  if (!Array.isArray(raw) || raw.length === 0) return '';
  const first = raw[0];
  if (first == null) return '';
  if (typeof first === 'string') return first;
  const content = (first as { content?: string }).content ?? (first as { value?: string }).value;
  return content != null ? String(content) : '';
};

// 回调地址：redirectUris 数组，统一取出 content/value 后展示
const getRedirectUrisDisplay = (record: Record<string, unknown>): { text: string; fullList: string[] } => {
  const raw = record.redirectUris ?? record.RedirectUris;
  if (!Array.isArray(raw) || raw.length === 0) return { text: '-', fullList: [] };
  const fullList = raw
    .map((item: unknown) => {
      if (item == null) return '';
      if (typeof item === 'string') return item;
      const content = (item as { content?: string }).content ?? (item as { value?: string }).value;
      return content != null ? String(content) : '';
    })
    .filter(Boolean);
  const text = fullList.length === 0 ? '-' : fullList.length === 1 ? fullList[0] : `共 ${fullList.length} 条`;
  return { text, fullList };
};

// 系统图标完整 URL（加 CDN 前缀）
const getLogoFullUrl = (logoUri: string | null | undefined): string => {
  if (!logoUri || typeof logoUri !== 'string') return '';
  const trimmed = logoUri.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `${CDN_PREFIX}${trimmed.replace(/^\//, '')}`;
};

// 表头配置（field_code 兼容接口 camelCase：clientId/clientName 等）
const TABLE_COLUMNS_CONFIG = [
  { field_code: 'clientId', field_name: '客户端ID', width: 120 },
  { field_code: 'clientName', field_name: '客户端名称', width: 140 },
  { field_code: 'requireClientSecret', field_name: '是否需要密钥', width: 120 },
  { field_code: 'clientSecrets', field_name: '客户端密钥', width: 140, showTooltip: true },
  { field_code: 'allowedScopes', field_name: '允许访问的资源', width: 160, showTooltip: true },
  { field_code: 'redirectUris', field_name: '回调地址', width: 160, showTooltip: true },
  { field_code: 'bindUserIds', field_name: '绑定用户', width: 120, showTooltip: true },
  { field_code: 'logoUri', field_name: '系统图标', width: 100 },
  { field_code: 'operationUserName', field_name: '操作人', width: 100 },
  { field_code: 'operationTime', field_name: '操作时间', width: 170 },
  { field_code: 'allowedGrantTypes', field_name: '授权类型', width: 120 },
];
const AdminRegister = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(null as null | string);
  const [searchParams, setSearchParams] = useState({
    ClientName: '',
    ClientId: '',
    CreateUserName: '',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = {
        clientName: searchParams.ClientName || '',
        clientId: searchParams.ClientId || '',
        createUserName: searchParams.CreateUserName || '',
      };
      const response = (await getAllClient(params)) as {
        res_status_code?: string;
        res_content?: unknown[];
        res_message?: string;
      };

      if (response?.res_status_code === '0' && response?.res_content != null) {
        const items = Array.isArray(response.res_content) ? response.res_content : [];
        setList(items);
        setPagination((prev) => ({ ...prev, total: items.length }));
      } else {
        message.error(response?.res_message || '获取列表失败');
      }
    } catch (error) {
      console.error('获取管理端注册列表失败:', error);
      message.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchList();
  };

  const handleReset = () => {
    setSearchParams({
      ClientName: '',
      ClientId: '',
      CreateUserName: '',
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setTimeout(() => fetchList(), 100);
  };

  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    setPagination((prev) => ({
      ...prev,
      current:
        newPagination.pageSize && newPagination.pageSize !== prev.pageSize
          ? 1
          : newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  const columns = [
    ...TABLE_COLUMNS_CONFIG.map((col) => {
      const isTime = col.field_code === 'operationTime';
      const needTooltip = col.showTooltip === true;
      return {
        title: col.field_name,
        dataIndex: col.field_code,
        key: col.field_code,
        width: col.width,
        ellipsis: needTooltip ? { showTitle: false } : false,
        render: (_: unknown, record: Record<string, unknown>) => {
          // 客户端密钥：取 clientSecrets?.[0].content
          if (col.field_code === 'clientSecrets') {
            const str = getClientSecretsDisplay(record);
            if (!str) return '-';
            return needTooltip ? (
              <Tooltip title={str} placement="topLeft">
                <span>{str}</span>
              </Tooltip>
            ) : (
              str
            );
          }
          // 回调地址：数组多条，优化展示
          if (col.field_code === 'redirectUris') {
            const { text, fullList } = getRedirectUrisDisplay(record);
            if (fullList.length <= 1) return text;
            return (
              <Tooltip
                title={<div style={{ whiteSpace: 'pre-wrap' }}>{fullList.join('\n')}</div>}
                placement="topLeft"
                overlayInnerStyle={{ minWidth: 420 }}
              >
                <span>{text}</span>
              </Tooltip>
            );
          }
          // 系统图标：CDN 前缀 + 小图 + 点击预览大图
          if (col.field_code === 'logoUri') {
            const logoUri = (getField(record, 'logoUri') as string) ?? (record.logoUri as string);
            const fullUrl = getLogoFullUrl(logoUri);
            if (!fullUrl) return '-';
            return (
              <img
                src={fullUrl}
                alt="系统图标"
                role="button"
                style={{
                  width: 32,
                  height: 32,
                  objectFit: 'contain',
                  cursor: 'pointer',
                  display: 'block',
                }}
                onClick={() => setPreviewLogo(fullUrl)}
              />
            );
          }

          let val = getField(record, col.field_code);
          if (col.field_code === 'requireClientSecret') {
            const n = val === 1 || val === '1' ? '是' : val === 0 || val === '0' ? '否' : null;
            return n ?? (val == null || val === '' ? '-' : String(val));
          }
          if (Array.isArray(val)) {
            val = val.join(', ');
          }
          if (val == null || val === '') return '-';
          if (isTime) {
            return dayjs(val as string).isValid() ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss') : String(val);
          }
          const str = String(val);
          if (needTooltip) {
            return (
              <Tooltip title={str} placement="topLeft">
                <span>{str}</span>
              </Tooltip>
            );
          }
          return str;
        },
      };
    }),
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_: unknown, record: Record<string, unknown>) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() =>
            navigate(
              `/admin-register/detail/${record.id ?? record.Id ?? record.clientId ?? (record as { clientId?: string }).clientId ?? ''}`
            )
          }
        >
          编辑
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-register-page">
      <div className="page-header">
        <h2>管理端注册</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin-register/new')}>
          新建
        </Button>
      </div>

      <div className="search-container">
        <Space size="middle" wrap>
          <Input
            placeholder="客户端ID"
            value={searchParams.ClientId}
            onChange={(e) => setSearchParams({ ...searchParams, ClientId: e.target.value })}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="客户端名称"
            value={searchParams.ClientName}
            onChange={(e) => setSearchParams({ ...searchParams, ClientName: e.target.value })}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="创建人"
            value={searchParams.CreateUserName}
            onChange={(e) => setSearchParams({ ...searchParams, CreateUserName: e.target.value })}
            style={{ width: 200 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
          
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={list.slice(
          (pagination.current - 1) * pagination.pageSize,
          pagination.current * pagination.pageSize
        )}
        rowKey={(r, index) =>
          (r as { id?: number; clientId?: string }).id ??
          (r as { id?: number; clientId?: string }).clientId ??
          `row-${index}`
        }
        loading={loading}
        size="small"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          size: 'small',
        }}
        onChange={handleTableChange}
        scroll={{ x: 1600, y: 'calc(100vh - 320px)' }}
      />
      <Modal
        open={!!previewLogo}
        footer={null}
        onCancel={() => setPreviewLogo(null)}
        width="auto"
        closable={true}
        maskClosable={true}
        styles={{
          content: { background: 'transparent', boxShadow: 'none' },
          body: { padding: 0, background: 'transparent', textAlign: 'center' },
        }}
      >
        {previewLogo && (
          <img src={previewLogo} alt="系统图标大图" style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block', margin: '0 auto' }} />
        )}
      </Modal>
    </div>
  );
};

export default AdminRegister;
